import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const initializedSections = new WeakSet<HTMLElement>();

const activateGoal = (section: HTMLElement, index: number): void => {
  section.dispatchEvent(
    new CustomEvent("homepage-goals:activate", {
      detail: { index },
    }),
  );
};

export const initHomepageGoalsScroll = (
  root: ParentNode = document,
): void => {
  if (typeof document === "undefined") return;

  root.querySelectorAll<HTMLElement>("[data-goal-selector]").forEach((section) => {
    if (initializedSections.has(section)) return;

    const items = section.querySelectorAll<HTMLElement>("[data-goal-item]");
    const center = section.querySelector<HTMLElement>(".hp-cel-center");
    if (items.length < 2 || !center) return;

    initializedSections.add(section);

    const media = gsap.matchMedia();

    media.add("(min-width: 1200px) and (prefers-reduced-motion: no-preference)", () => {
      let activeIndex = -1;
      let isClickScroll = false;

      const setActiveFromProgress = (progress: number): void => {
        if (isClickScroll) return;

        const nextIndex = Math.min(
          items.length - 1,
          Math.floor(progress * (items.length - 1)),
        );
        if (nextIndex === activeIndex) return;

        activeIndex = nextIndex;
        activateGoal(section, nextIndex);
      };

      const scrollToIndex = (index: number): void => {
        const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
        const step = 1 / (items.length - 1);
        const progress =
          clampedIndex === items.length - 1
            ? 1
            : clampedIndex === 0
              ? 0
              : clampedIndex * step + step / 2;
        const targetScroll = trigger.start + (trigger.end - trigger.start) * progress;

        isClickScroll = true;
        activeIndex = clampedIndex;
        activateGoal(section, clampedIndex);

        window.scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });

        window.setTimeout(() => {
          isClickScroll = false;
          setActiveFromProgress(trigger.progress);
        }, 700);
      };

      const trigger = ScrollTrigger.create({
        trigger: center,
        start: "center center",
        end: () => `+=${(items.length - 1) * Math.max(165, window.innerHeight * 0.24)}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onEnter: () => setActiveFromProgress(0),
        onEnterBack: (self) => setActiveFromProgress(self.progress),
        onUpdate: (self) => setActiveFromProgress(self.progress),
        onLeave: () => setActiveFromProgress(1),
        onLeaveBack: () => setActiveFromProgress(0),
      });

      const handleScrollRequest = (event: Event): void => {
        const { index } = (event as CustomEvent<{ index?: number }>).detail || {};
        if (typeof index !== "number") return;

        event.preventDefault();
        scrollToIndex(index);
      };

      section.addEventListener("homepage-goals:scroll-to-index", handleScrollRequest);

      return () => {
        section.removeEventListener("homepage-goals:scroll-to-index", handleScrollRequest);
        trigger.kill();
        activeIndex = -1;
        isClickScroll = false;
      };
    });
  });
};
